import type { LocalizedString } from './studio';
import {
  LEGAL_IMPRINT_FIELD_KEYS,
  LEGAL_PRIVACY_FIELD_KEYS,
} from './legalBlockFields';

/** Default site-facing imprint `<dt>` labels (overridable via `label_<fieldKey>` CDL). */
export const LEGAL_IMPRINT_DEFAULT_LABELS: Record<
  (typeof LEGAL_IMPRINT_FIELD_KEYS)[number],
  LocalizedString
> = {
  legalName: { de: 'Anbieter', en: 'Provider', vi: 'Nhà cung cấp' },
  representative: {
    de: 'Vertretungsberechtigt',
    en: 'Authorized representative',
    vi: 'Người đại diện',
  },
  address: { de: 'Anschrift', en: 'Address', vi: 'Địa chỉ' },
  contactPhone: { de: 'Telefon', en: 'Phone', vi: 'Điện thoại' },
  contactEmail: { de: 'E-Mail', en: 'Email', vi: 'Email' },
  registerCourt: { de: 'Registergericht', en: 'Register court', vi: 'Tòa đăng ký' },
  registerNumber: { de: 'Registernummer', en: 'Register number', vi: 'Số đăng ký' },
  vatId: { de: 'USt-IdNr.', en: 'VAT ID', vi: 'Mã số thuế' },
  widerrufText: { de: 'Widerrufsrecht', en: 'Right of withdrawal', vi: 'Quyền hủy' },
  productQualityText: {
    de: 'Lebensmittelkennzeichnung & Allergene',
    en: 'Food labelling & allergens',
    vi: 'Ghi nhãn thực phẩm & dị ứng',
  },
};

/** Default site-facing privacy `<dt>` labels (overridable via `label_<fieldKey>` CDL). */
export const LEGAL_PRIVACY_DEFAULT_LABELS: Record<
  (typeof LEGAL_PRIVACY_FIELD_KEYS)[number],
  LocalizedString
> = {
  controllerName: { de: 'Verantwortlicher', en: 'Controller', vi: 'Đơn vị chịu trách nhiệm' },
  controllerAddress: { de: 'Anschrift', en: 'Address', vi: 'Địa chỉ' },
  contactEmail: {
    de: 'E-Mail Datenschutz',
    en: 'Privacy e-mail',
    vi: 'Email bảo mật',
  },
  purposes: {
    de: 'Zwecke der Verarbeitung',
    en: 'Purposes of processing',
    vi: 'Mục đích xử lý',
  },
  legalBasis: { de: 'Rechtsgrundlagen', en: 'Legal bases', vi: 'Cơ sở pháp lý' },
  retention: { de: 'Speicherdauer', en: 'Retention period', vi: 'Thời gian lưu trữ' },
  rights: { de: 'Ihre Rechte als betroffene Person', en: 'Your rights as a data subject', vi: 'Quyền của bạn' },
};

/** CDL content key for an editable legal field label. */
export function legalFieldLabelContentKey(fieldKey: string): string {
  return `label_${fieldKey}`;
}

export function isLegalFieldLabelContentKey(key: string): boolean {
  return key.startsWith('label_');
}

/** Inverse of `legalFieldLabelContentKey` — `label_legalName` → `legalName`. */
export function legalFieldKeyFromLabelContentKey(key: string): string | null {
  if (!isLegalFieldLabelContentKey(key)) {
    return null;
  }
  const fieldKey = key.slice('label_'.length);
  return fieldKey || null;
}
